// API calls for the Agency Fulfilment Call Bot (STSMPD-1322, STSMPD-1442).
//
// These go through flex-core's admin proxy (ACP -> flex-core -> fulfilment-call-service),
// NOT directly to the service. ACP cannot reach the service from the browser (no
// browser-resolvable host; the service's public host is WAF-locked to the Vapi webhook),
// so per SRE flex-core proxies the call over the service mesh. We therefore build these
// as normal flex-core /api/v2 requests via `apiRequest`, reusing the standard auth +
// response handling (`handleCall`).
//
//   POST /api/v2/admin/call_requests      -> create a call request (places the call)
//   GET  /api/v2/admin/call_requests/:id  -> poll status / outcome

import { handleCall, apiRequest } from 'syft-acp-core/api/call'
import config from 'syft-acp-core/config'

import { CreateCallRequestPayload, CreateCallResponse, CallRequestView } from './types'

/** v1 scenario shipped for the Phase 1 PoC ("late-notice fill check"). */
export const DEFAULT_SCRIPT_ID = 'late_notice_fill_check_v1'

const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.trim() !== ''

/**
 * True when the call bot is configured to place a call AND record its outcome.
 *
 * The request is proxied through flex-core (always reachable), so we no longer gate on a
 * service URL. We still require the Slack channel: the call-request contract treats
 * `REACT_APP_FULFILMENT_CALL_SLACK_CHANNEL` as required (the service posts the durable
 * outcome to Slack), so a deploy without it would place calls whose outcome can't be
 * delivered. The action is gated on this so we never trigger a half-configured call.
 */
export const isCallServiceConfigured = (): boolean => isNonEmptyString(config.fulfilmentCallSlackChannel)

// handleCall resolves with `{ payload, response, meta }`; callers here only want the payload.
const unwrap =
  <T>(resolve: (value: T) => void) =>
  (result: { payload: T }) =>
    resolve(result.payload)

/** POST /api/v2/admin/call_requests — create a call request (and place the call if enabled). */
export const createCallRequest = (payload: CreateCallRequestPayload): Promise<CreateCallResponse> =>
  new Promise((resolve, reject) => {
    handleCall(
      apiRequest({ path: '/admin/call_requests', reqArgs: payload, method: 'POST' }),
      unwrap<CreateCallResponse>(resolve),
      reject,
    )
  })

/** GET /api/v2/admin/call_requests/:id — poll the current state of a call request. */
export const getCallRequest = (callRequestId: string): Promise<CallRequestView> =>
  new Promise((resolve, reject) => {
    handleCall(
      apiRequest({ path: `/admin/call_requests/${callRequestId}`, method: 'GET' }),
      unwrap<CallRequestView>(resolve),
      reject,
    )
  })
